import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  FilterFn,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

// Define props interface
interface TableContainerProps {
  columns: any[];
  data: any[];
  isGlobalFilter?: boolean;
  customPageSize?: number;
  iscustomPageSize?: boolean;  // Add this line
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  SearchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  isBordered?: boolean;  // Add this line as well, since it's used in RecomendedJobs.tsx
  onSort?: (column: string) => void;  // Make onSort optional
  sortBy?: { id: string; desc: boolean };  // Make sortBy optional
  showTopPagination?: boolean;
}

// DebouncedInput component
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
};

const GlobalFilter = ({
  globalFilter,
  setGlobalFilter,
  SearchPlaceholder,
  onSearchChange,
}: {
  globalFilter: string | undefined;
  setGlobalFilter: (filterValue: string | undefined) => void;
  SearchPlaceholder: string;
  onSearchChange?: (value: string) => void;
}) => {
  const [value, setValue] = React.useState(globalFilter ?? '');

  const onChange = (newValue: string | number) => {
    const stringValue = String(newValue);
    setGlobalFilter(stringValue || undefined);
    setValue(stringValue);
    if (onSearchChange) {
      onSearchChange(stringValue);
    }
  };

  return (
    <DebouncedInput
      value={value || ""}
      onChange={onChange}
      className="form-control search"
      placeholder={SearchPlaceholder}
    />
  );
};

const TableContainer: React.FC<TableContainerProps> = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  iscustomPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder = "Search...",
  onSearchChange,
  isBordered,
  onSort,
  sortBy,
  showTopPagination = false
}) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  

  useEffect(() => {
    if (iscustomPageSize && customPageSize) {
      table.setPageSize(Number(customPageSize));
    }
  }, [iscustomPageSize, customPageSize, table]);

  const renderResultsCount = () => (
    <div className="text-muted mb-2">
      Showing <span className="fw-semibold">{table.getState().pagination.pageSize}</span> of <span className="fw-semibold">{data.length}</span> Results
    </div>
  );

  const renderPaginationControls = () => {
    const currentPage = table.getState().pagination.pageIndex;
    const totalPages = table.getPageCount();
    const maxVisiblePages = 7; // Adjust this number as needed
  
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);
  
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }
  
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
  
    return (
      <div className="pagination-container d-flex justify-content-end">
        <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
          <li className={!table.getCanPreviousPage() ? "page-item disabled" : "page-item"}>
            <Button color="link" className="page-link" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>Previous</Button>
          </li>
          {pageNumbers.map((page) => (
            <li key={page} className="page-item">
              <Button 
                color="link" 
                className={currentPage === page ? "active" : ""} 
                onClick={() => table.setPageIndex(page)}
              >
                {page + 1}
              </Button>
            </li>
          ))}
          <li className={!table.getCanNextPage() ? "page-item disabled" : "page-item"}>
            <Button color="link" className="page-link" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Next</Button>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <Fragment>
      {isGlobalFilter && (
        <Row className="mb-2">
          <Col sm={4}>
            <GlobalFilter
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              SearchPlaceholder={SearchPlaceholder}
              onSearchChange={onSearchChange}
            />
          </Col>
        </Row>
      )}

      {showTopPagination && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          {renderResultsCount()}
          {renderPaginationControls()}
        </div>
      )}

      <div className={divClass}>
        <table className={tableClass}>
        <thead className={theadClass}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.accessorKey}
                onClick={() => column.sortable && onSort && onSort(column.accessorKey)}
                style={{ cursor: column.sortable && onSort ? 'pointer' : 'default' }}
              >
                {column.header}
                {column.sortable && sortBy && sortBy.id === column.accessorKey && (
                  <span>{sortBy.desc ? ' 🔽' : ' 🔼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        {renderResultsCount()}
        {renderPaginationControls()}
      </div>
    </Fragment>
  );
};

export default TableContainer;